/* eslint-disable */
// AttachPicker — one field, four families. A real combobox, not a hover list.
//
// ⚠️ THE ROWS HERE ARE ALREADY-FETCHED ENTITIES, so the picker shows the same
// picture the recipient will see. What leaves this component is only
// {kind, id} — toPayloadRef strips everything else. The code and the picture
// exist to help the sender choose; they are never written into the message.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAttachmentSearch } from '../../hooks/useAttachmentSearch';
import { ATTACH_MAX, familyFor } from '../../utils/attachmentRegistry';
import AttachmentCard from './AttachmentCard';
import styles from './attachments.module.css';

export function AttachPicker({ attached, onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef(null);
  const wrapRef = useRef(null);
  const { groups, busy, minChars } = useAttachmentSearch(q, attached);

  const full = attached.length >= ATTACH_MAX;

  // A flat list of the rows, so ↑/↓ can walk across group headers.
  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  useEffect(() => { setCursor(0); }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const add = (row) => {
    if (attached.length >= ATTACH_MAX) return;
    if (attached.some((a) => a.kind === row.kind && a.id === row.id)) return;
    onChange([...attached, row]);
    setQ('');
    setOpen(false);
  };

  const removeAt = (i) => onChange(attached.filter((_, n) => n !== i));

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); add(flat[cursor]); }
  };

  const showList = open && q.trim().length >= minChars;
  const nothing = showList && !busy && groups.length === 0;

  return (
    <div className={styles.picker} ref={wrapRef}>
      <input
        ref={boxRef}
        className={styles.search}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls="attach-results"
        aria-autocomplete="list"
        aria-label="Search a model, development or lens to attach"
        placeholder={full ? 'Four is the limit.' : 'Search a model, development, lens…'}
        disabled={full}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        data-test="attach-search"
      />

      {showList && (
        <div className={styles.results} id="attach-results" role="listbox">
          {nothing && <div className={styles.resEmpty}>Nothing matches “{q.trim()}”</div>}
          {groups.map((g) => (
            <div key={g.kind}>
              <div className={styles.resGroup}>
                {g.label}
                {!g.failed && <em>{g.rows.length}</em>}
              </div>
              {g.failed ? (
                <div className={styles.resFail} data-test={'attach-failed-' + g.kind}>
                  Couldn&apos;t search {g.label.toLowerCase()}
                </div>
              ) : g.rows.map((row) => {
                const i = flat.indexOf(row);
                return (
                  <div
                    key={row.kind + row.id}
                    role="option"
                    aria-selected={i === cursor}
                    className={styles.resRow + (i === cursor ? ' ' + styles.resCursor : '')}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => add(row)}
                    data-test={'attach-result-' + row.code}
                  >
                    <AttachmentCard entity={row} density="thumb" />
                    <span
                      className={styles.pill}
                      style={{
                        color: familyFor(row.kind).pill.fg,
                        background: familyFor(row.kind).pill.bg,
                        borderColor: familyFor(row.kind).pill.bd,
                      }}
                    >
                      {familyFor(row.kind).label}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {attached.length > 0 && (
        <div className={styles.attached}>
          {attached.map((row, i) => (
            <div className={styles.attRow} key={row.kind + row.id} data-test={'attach-picked-' + row.code}>
              <AttachmentCard entity={row} density="card" />
              <button
                type="button"
                className={styles.x}
                onClick={() => removeAt(i)}
                aria-label={'Remove ' + row.code}
                data-test={'attach-remove-' + row.code}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.hint}>
        {full
          ? 'Four is the limit.'
          : `Up to ${ATTACH_MAX}. Each one becomes a card the reader can open.`}
      </div>
    </div>
  );
}

export default AttachPicker;
